import React from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, DownloadIcon, UsersIcon, ListChecksIcon, HardHatIcon, WrenchIcon, AlertOctagonIcon, PlusCircleIcon, FileWarningIcon, CheckCircle2Icon, XCircleIcon, ClipboardCheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps, ECMDriver } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
import { SignaturePad } from '../ui/SignaturePad';
import { useI18n } from '../../config/i18n';
// Derive key risks from actual step hazard data
function buildKeyRisks(data: any): {
  risk: string;
  driver: ECMDriver;
}[] {
  const risks: {
    risk: string;
    driver: ECMDriver;
  }[] = [];
  const steps = data.stepHazardData || [];
  const general = data.generalHazards || [];
  // Pick the most relevant hazard from each step (first relevant one)
  for (const step of steps) {
    const topHazard = (step.hazards || []).find((h: any) => h.relevant);
    if (topHazard) {
      risks.push({
        risk: `${topHazard.hazard} — ${topHazard.controls?.eliminateSubstitute || 'follow controls'}`,
        driver: topHazard.ecmDriver || 'E'
      });
    }
  }
  // Add top general hazards
  for (const gh of general.filter((h: any) => h.relevant).slice(0, 2)) {
    risks.push({
      risk: `${gh.hazard} — ${gh.controls?.eliminateSubstitute || 'follow controls'}`,
      driver: gh.ecmDriver || 'E'
    });
  }
  // Limit to top 5
  return risks.slice(0, 5);
}
export function PTPSummaryScreen({
  data,
  updateData,
  onBack,
  onNext,
  onExport
}: ScreenProps) {
  const {
    t
  } = useI18n();
  const AI_KEY_RISKS = buildKeyRisks(data);
  const totalSteps = data.stepHazardData.length;
  const totalHazards = data.stepHazardData.reduce((acc, s) => acc + s.hazards.filter(h => h.relevant).length, 0) + data.generalHazards.filter(h => h.relevant).length;
  const allPPE = Array.from(new Set([...data.stepHazardData.flatMap(s => s.hazards.filter(h => h.relevant).map(h => h.controls.ppe)), ...data.generalHazards.filter(h => h.relevant).map(h => h.controls.ppe)].join(', ').split(',').map(p => p.trim()).filter(p => p && p !== 'N/A' && p !== 'Standard PPE')));
  const allEquipmentCount = new Set([...data.generalEquipment, ...data.stepHazardData.flatMap(s => s.hazards.flatMap(h => h.equipment))]).size;
  // Permits analysis
  const neededPermits = (data.permits || []).filter(p => p.needed);
  const missingPermits = neededPermits.filter(p => p.exists === false);
  const pendingPermits = neededPermits.filter(p => p.exists === null);
  const hasPermitProblems = missingPermits.length > 0;
  const getDriverDot = (d: ECMDriver) => {
    switch (d) {
      case 'E':
        return 'bg-emerald-500';
      case 'C':
        return 'bg-blue-500';
      case 'M':
        return 'bg-amber-500';
    }
  };
  return <div className="flex flex-col h-full" data-id="element-759">
      <div className="flex items-center gap-3 mb-4" data-id="element-760">
        <ShieldCheckIcon className="w-7 h-7 text-yellow-500" data-id="element-761" />
        <div data-id="element-762">
          <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-763">
            Pre-Task Plan Summary
          </h2>
          <p className="text-gray-400 text-[11px]" data-id="element-764">AI analysis complete</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-20 -mx-1 px-1 space-y-4" data-id="element-765">
        {/* Task Info */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-766">
          <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-767">
            <div data-id="element-768">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-769">
                Project
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-770">
                {data.project}
              </p>
            </div>
            <div data-id="element-771">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-772">
                Date
              </p>
              <p className="text-gray-900 font-medium text-sm" data-id="element-773">{data.date}</p>
            </div>
          </div>
          <div className="pt-2 border-t border-gray-200" data-id="element-774">
            <p className="text-yellow-500 font-bold text-sm" data-id="element-775">
              {data.taskTitle || 'Pre-Task Plan'}
            </p>
            <p className="text-gray-400 text-xs mt-0.5" data-id="element-776">{data.location}</p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-1.5" data-id="element-777">
          {[{
          icon: <ListChecksIcon className="w-3.5 h-3.5 text-yellow-500" data-id="element-778" />,
          value: totalSteps,
          label: 'Steps',
          delay: 0.05
        }, {
          icon: <AlertTriangleIcon className="w-3.5 h-3.5 text-red-500" data-id="element-779" />,
          value: totalHazards,
          label: 'Hazards',
          delay: 0.1
        }, {
          icon: <UsersIcon className="w-3.5 h-3.5 text-blue-500" data-id="element-780" />,
          value: data.crewSize,
          label: 'Crew',
          delay: 0.15
        }, {
          icon: <WrenchIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-781" />,
          value: allEquipmentCount,
          label: 'Tools',
          delay: 0.2
        }].map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: stat.delay
        }} className="bg-white border border-gray-200 rounded-lg px-2 py-2 flex items-center gap-1.5" data-id="element-782">
              {stat.icon}
              <span className="text-base font-bold text-gray-900" data-id="element-783">
                {stat.value}
              </span>
              <span className="text-[9px] text-gray-400 uppercase font-semibold" data-id="element-784">
                {stat.label}
              </span>
            </motion.div>)}
        </div>

        {/* Missing Permits Warning */}
        {hasPermitProblems && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-red-50 border border-red-300 rounded-xl p-4" data-id="element-785">
            <div className="flex items-center gap-2 mb-2" data-id="element-786">
              <FileWarningIcon className="w-5 h-5 text-red-500" data-id="element-787" />
              <p className="text-sm font-bold text-red-600" data-id="element-788">
                {t('permits.missingWarning')}
              </p>
            </div>
            <div className="space-y-1.5" data-id="element-789">
              {missingPermits.map((permit, i) => <div key={i} className="flex items-center gap-2" data-id="element-790">
                  <XCircleIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" data-id="element-791" />
                  <span className="text-red-700 text-sm" data-id="element-792">{permit.name}</span>
                </div>)}
            </div>
            <p className="text-red-600 text-xs mt-2 italic" data-id="element-793">
              These permits must be obtained before work can begin.
            </p>
          </motion.div>}

        {/* Key Points */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="bg-red-50 border border-red-200 rounded-xl p-4" data-id="element-794">
          <div className="flex items-center gap-2 mb-3" data-id="element-795">
            <AlertOctagonIcon className="w-4 h-4 text-red-500" data-id="element-796" />
            <p className="text-sm font-bold text-red-600" data-id="element-797">Key Points</p>
          </div>
          <div className="space-y-2.5" data-id="element-798">
            {AI_KEY_RISKS.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.08
          }} className="flex items-start gap-2.5" data-id="element-799">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getDriverDot(item.driver)}`} data-id="element-800" />
                <p className="text-sm text-gray-800 leading-snug" data-id="element-801">
                  {item.risk}
                </p>
              </motion.div>)}
          </div>
        </motion.div>

        {/* Permits Section */}
        {neededPermits.length > 0 && <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-802">
            <div className="flex items-center gap-2 mb-2" data-id="element-803">
              <ClipboardCheckIcon className="w-4 h-4 text-yellow-500" data-id="element-804" />
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-805">
                Required Permits
              </p>
            </div>
            <div className="space-y-2" data-id="element-806">
              {neededPermits.map((permit, i) => <div key={i} className={`flex items-center justify-between px-2.5 py-2 rounded-lg border ${permit.exists === false ? 'bg-red-50 border-red-200' : permit.exists === true ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-807">
                  <span className={`text-sm font-medium ${permit.exists === false ? 'text-red-700' : permit.exists === true ? 'text-green-700' : 'text-amber-700'}`} data-id="element-808">
                    {permit.name}
                  </span>
                  <div className="flex items-center gap-1.5" data-id="element-809">
                    {permit.exists === true && <>
                        <CheckCircle2Icon className="w-4 h-4 text-green-500" data-id="element-810" />
                        <span className="text-green-600 text-[10px] font-bold uppercase" data-id="element-811">
                          {t('permits.exists')}
                        </span>
                      </>}
                    {permit.exists === false && <>
                        <XCircleIcon className="w-4 h-4 text-red-500" data-id="element-812" />
                        <span className="text-red-500 text-[10px] font-bold uppercase" data-id="element-813">
                          {t('permits.missing')}
                        </span>
                      </>}
                    {permit.exists === null && <span className="text-amber-600 text-[10px] font-bold uppercase" data-id="element-814">
                        {t('permits.pendingStatus')}
                      </span>}
                  </div>
                </div>)}
            </div>
          </div>}

        {/* PPE Required */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-815">
          <div className="flex items-center gap-2 mb-2" data-id="element-816">
            <HardHatIcon className="w-4 h-4 text-yellow-500" data-id="element-817" />
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-818">
              Required PPE
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-819">
            {allPPE.map((item, i) => <span key={i} className="bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full text-xs font-medium" data-id="element-820">
                {item}
              </span>)}
          </div>
        </div>

        {/* Steps Quick List */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-821">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-822">
            Method of Procedure (MOP)
          </p>
          <div className="space-y-1.5" data-id="element-823">
            {data.stepHazardData.map((step, i) => {
            const relevantCount = step.hazards.filter(h => h.relevant).length;
            return <div key={i} className="flex items-center gap-2" data-id="element-824">
                  <span className="text-yellow-500 font-bold text-xs w-5 text-right flex-shrink-0" data-id="element-825">
                    {i + 1}.
                  </span>
                  <span className="text-gray-700 text-xs flex-1 truncate" data-id="element-826">
                    {step.description}
                  </span>
                  <span className="text-gray-400 text-[10px] flex-shrink-0" data-id="element-827">
                    {relevantCount} hazard{relevantCount !== 1 ? 's' : ''}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Crew */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-828">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-829">
            Crew ({data.crewSize})
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-830">
            {data.crewRoles.map((role, i) => <span key={i} className="bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full text-xs font-medium" data-id="element-831">
                {role.name} · {role.role}
              </span>)}
          </div>
        </div>

        {/* Meeting Additions */}
        {data.meetingAdditions && data.meetingAdditions.length > 0 && <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-832">
            <div className="flex items-center gap-2 mb-2" data-id="element-833">
              <PlusCircleIcon className="w-4 h-4 text-blue-500" data-id="element-834" />
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold" data-id="element-835">
                Meeting Additions
              </p>
            </div>
            <div className="space-y-2" data-id="element-836">
              {data.meetingAdditions.map((item, i) => <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-200" data-id="element-837">
                  <div className="flex items-center gap-1.5 mb-1" data-id="element-838">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${item.type === 'hazard' ? 'bg-red-50 text-red-600' : item.type === 'control' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`} data-id="element-839">
                      {item.type}
                    </span>
                    <span className="text-[10px] text-gray-400" data-id="element-840">
                      {item.stepRef}
                    </span>
                  </div>
                  <p className="text-gray-700 text-xs font-medium" data-id="element-841">
                    {item.title}
                  </p>
                </div>)}
            </div>
          </div>}

        <p className="text-gray-400 text-[10px] text-center italic" data-id="element-842">
          Full details available in the exported PDF or by going back to edit
          individual steps.
        </p>

        {/* Signature Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mt-6" data-id="element-843">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2" data-id="element-844">
            {t('signature.title')}
          </p>
          <p className="text-gray-500 text-xs mb-4 leading-relaxed" data-id="element-845">
            {t('signature.disclaimer')}
          </p>
          <SignaturePad onSignatureChange={dataUrl => updateData({
          foremanSignature: dataUrl || undefined
        })} data-id="element-846" />
        </div>

        <div className="h-12" data-id="element-847" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 pt-3 pb-0 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent space-y-2" data-id="element-848">
        <PrimaryButton onClick={onNext} disabled={!data.foremanSignature} icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-850" />} data-id="element-849">
          {data.foremanSignature ? t('signature.finalize') : t('signature.required')}
        </PrimaryButton>
        <div className="flex gap-2" data-id="element-851">
          <SecondaryButton onClick={() => onExport?.()} icon={<DownloadIcon className="w-4 h-4" data-id="element-853" />} data-id="element-852">
            {t('signature.exportPdf')}
          </SecondaryButton>
          <SecondaryButton onClick={onBack} data-id="element-854">
            {t('signature.editPlan')}
          </SecondaryButton>
        </div>
      </div>
    </div>;
}